import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import { Link, useLocation, type LinkProps } from 'react-router';

import { buttonClasses, type Size, type Variant } from './buttonStyles';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  /**
   * React 19 passes `ref` to a function component as an ordinary prop, but
   * `ButtonHTMLAttributes` does not declare it. Declared here so a caller can
   * hold the element — the mobile drawer returns focus to its toggle.
   */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A navigation that sits among actions ("Open reports", "Open queue").
 *
 * It is a LINK — it goes somewhere and commits nothing — so it is announced as
 * one and opens in a new tab like one, but it takes the button's R-050 shape so
 * a page header's controls read as one row. Never `primary` for something that
 * merely navigates away from a decision.
 *
 * Like `RecordLink`, it leaves the page's own address as `state.from` (B-08),
 * unless the caller passes a `state` of its own, so a record it opens can send
 * the person back to the view they left.
 */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  className,
  state,
  ...rest
}: LinkProps & { variant?: Variant; size?: Size }) {
  const location = useLocation();
  const carried: unknown = state ?? { from: `${location.pathname}${location.search}` };

  return (
    <Link
      data-variant={variant}
      className={buttonClasses(variant, size, className)}
      state={carried}
      {...rest}
    />
  );
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  onClick,
  'aria-disabled': ariaDisabled,
  ...rest
}: ButtonProps) {
  /*
   * FORM-01 — while it loads, a button ignores activation whatever `disabled`
   * says: a click, Enter, Space and a form's implicit submission all arrive
   * here as a click, and do nothing. It says so (`aria-disabled`, `aria-busy`).
   * The native `disabled` stays what the caller asked for — when it gave none,
   * `loading` still disables natively, as before; when it said `false`, the
   * button stays focusable, because Chromium moves focus off a focused control
   * the moment it becomes disabled (K-7, PlatformOrganisationPage).
   */
  const onPress = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <button
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      // A5: the variant, readable by tests. `primary` and `exception` share one fill.
      data-variant={variant}
      className={buttonClasses(variant, size, className)}
      {...rest}
      aria-disabled={loading ? true : ariaDisabled}
      onClick={onPress}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
